import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { LM, THRESHOLDS } from '@erectus/shared';
import type { FrameRecord, MinutePoint, SessionSummary } from '@erectus/shared';
import { dequantize, loadSnapshots } from '@/session/db.js';
import { minuteSeries, snapshotsFrom, type Snapshot } from '@/scoring/hourline.js';
import { PORTRAIT_IDEAL } from '@/lib/camera.js';
import { scoreColor } from '@/ui/score.js';
import { Button, Card, SectionTitle } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { ShareNudge, useReplay } from './useReplay.js';
import { HEAD_PER_TORSO } from './replayModel.js';

/**
 * How posture moved through one session, beyond its single score: neck angle
 * and trunk lean minute by minute, and the MediaPipe skeleton every ten
 * minutes with that block's numbers.
 *
 * The lines come from the summary's per-minute series (any device, any age)
 * or, for older sessions, from frames still on this device. The skeletons are
 * redrawn from stored landmarks — no photo is ever kept (CLAUDE.md
 * principle 1) — and live on this device only.
 *
 * The whole session can also be replayed as a moving skeleton, sped up
 * (Replay.tsx): from the frames on the phone that recorded it, or, for a
 * shared session, from the user's keypoint file (useReplay.tsx). A ten-minute
 * card opens the replay at its moment.
 */
export function ThroughSession({
  sessionId,
  summary,
  frames,
}: {
  sessionId: string;
  summary: SessionSummary;
  frames: readonly FrameRecord[];
}): ReactNode {
  const series = useMemo(
    () => summary.series ?? (frames.length ? minuteSeries(frames) : []),
    [summary.series, frames],
  );
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const replay = useReplay(sessionId, summary, frames);
  const canReplay = replay.source !== null;

  useEffect(() => {
    let cancelled = false;
    void loadSnapshots(sessionId)
      .catch(() => [])
      .then((stored) => {
        if (cancelled) return;
        // Sessions from before snapshots were saved: derive them while the frames last.
        setSnapshots(stored.length ? stored : snapshotsFrom(frames, summary.sampleFps));
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, frames, summary.sampleFps]);

  // Not on this phone, but shared to the account: draw the cards from that file.
  const { prefetch, sharedFrames, source } = replay;
  const prefetched = useRef(false);
  useEffect(() => {
    if (snapshots === null || snapshots.length > 0 || source !== 'account') return;
    if (sharedFrames) setSnapshots(snapshotsFrom(sharedFrames, summary.sampleFps));
    else if (!prefetched.current) {
      prefetched.current = true; // once: a failed fetch shows its error, it does not loop
      prefetch();
    }
  }, [snapshots, source, sharedFrames, prefetch, summary.sampleFps]);

  if (series.length < 2 && (snapshots?.length ?? 0) === 0 && !canReplay) return null;
  const minutes = Math.max(1, Math.ceil(summary.durationSec / 60));

  return (
    <Card id="through" className="scroll-mt-4 space-y-4">
      <SectionTitle>Through the session</SectionTitle>
      {series.length >= 2 ? (
        <div className="space-y-3">
          <LineChart
            title="Neck angle"
            unit="°"
            hint={`${THRESHOLDS.cva.good}° and above is good`}
            points={series.map((p) => [p.m, p.cva ?? null])}
            minutes={minutes}
            goodBand={[THRESHOLDS.cva.good, 90]}
            domain={[25, 75]}
          />
          <LineChart
            title="Trunk lean"
            unit="°"
            hint={`${THRESHOLDS.trunkInclination.goodMin}° to +${THRESHOLDS.trunkInclination.goodMax}° is good`}
            points={series.map((p) => [p.m, p.trunk])}
            minutes={minutes}
            goodBand={[THRESHOLDS.trunkInclination.goodMin, THRESHOLDS.trunkInclination.goodMax]}
            domain={[-20, 35]}
          />
          <GoodStrip series={series} minutes={minutes} />
        </div>
      ) : null}

      {snapshots && snapshots.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-muted">
            Every five minutes — the skeleton MediaPipe saw, and how those five minutes went.{' '}
            {source === 'account' ? 'From the keypoints you share to your account.' : 'Kept on this phone only.'}
            {canReplay ? ' Tap one to replay from there.' : ''}
          </p>
          <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1">
            {snapshots.map((snap) => (
              <SnapshotCard
                key={snap.blockStart}
                snap={snap}
                startedAt={summary.startedAt}
                onOpen={canReplay ? () => replay.open('snapshot', snap.t) : undefined}
              />
            ))}
          </div>
        </div>
      ) : null}

      {canReplay ? (
        <div className="space-y-1">
          <Button
            variant="secondary"
            className="w-full"
            disabled={replay.loading}
            onClick={() => replay.open('button', 0)}
          >
            <Icon name="play" size={18} />
            {replay.loading ? 'Loading the replay…' : 'Watch the replay'}
          </Button>
          <p className="text-center text-[11px] text-muted">
            The whole session as a moving skeleton, at {replay.speed}× — redrawn from the joints, no video.
          </p>
          {replay.error ? (
            <p role="alert" className="text-center text-xs text-[color:var(--color-state-slouch)]">
              {replay.error}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="rounded-xl bg-surface p-3 text-xs text-muted">
          <strong className="font-semibold text-ink-soft">Skeleton replay isn’t available for this session.</strong>{' '}
          The replay is redrawn from the joints saved on the phone that recorded it, and that phone keeps them for
          7 days. Open this report on that phone within a week — or turn on detection-improvement sharing, which
          keeps the joints in your account so the replay opens anywhere.
        </p>
      )}

      {replay.source !== 'account' ? <ShareNudge /> : null}

      {replay.element}
    </Card>
  );
}

// ── charts ────────────────────────────────────────────────────────────────

const W = 300;
const H = 84;

export function LineChart({
  title,
  unit,
  hint,
  points,
  minutes,
  goodBand,
  domain,
}: {
  title: string;
  unit: string;
  hint: string;
  points: Array<[number, number | null]>;
  minutes: number;
  goodBand: [number, number];
  domain: [number, number];
}): ReactNode {
  const values = points.map(([, v]) => v).filter((v): v is number => v !== null);
  if (values.length < 2) return null;
  const lo = Math.min(domain[0], ...values);
  const hi = Math.max(domain[1], ...values);
  const x = (m: number): number => (m / Math.max(1, minutes - 1)) * W;
  const y = (v: number): number => H - ((v - lo) / (hi - lo)) * H;

  // Break the line where a minute had nothing measurable.
  const segments: string[] = [];
  let current = '';
  for (const [m, v] of points) {
    if (v === null) {
      if (current) segments.push(current);
      current = '';
      continue;
    }
    current += `${current ? 'L' : 'M'}${x(m).toFixed(1)} ${y(v).toFixed(1)}`;
  }
  if (current) segments.push(current);

  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const bandTop = y(Math.min(hi, goodBand[1]));
  const bandBottom = y(Math.max(lo, goodBand[0]));

  return (
    <figure className="space-y-1">
      <figcaption className="flex items-baseline justify-between text-xs">
        <span className="font-semibold text-ink">{title}</span>
        <span className="text-muted">
          avg {avg.toFixed(0)}
          {unit} · {hint}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-20 w-full overflow-visible"
        role="img"
        aria-label={`${title} per minute, average ${avg.toFixed(0)}${unit}`}
      >
        <rect x={0} y={bandTop} width={W} height={Math.max(0, bandBottom - bandTop)} fill="var(--color-state-good-soft)" />
        {Array.from({ length: Math.floor((minutes - 1) / 10) }, (_, i) => (i + 1) * 10).map((m) => (
          <line key={m} x1={x(m)} x2={x(m)} y1={0} y2={H} stroke="var(--color-line)" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
        ))}
        {segments.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="var(--color-ink)" strokeWidth={1.8} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
    </figure>
  );
}

/** Share of each minute spent in good posture, as one strip. */
export function GoodStrip({ series, minutes }: { series: readonly MinutePoint[]; minutes: number }): ReactNode {
  const byMinute = new Map(series.map((p) => [p.m, p]));
  return (
    <figure className="space-y-1">
      <figcaption className="flex justify-between text-xs">
        <span className="font-semibold text-ink">Good posture, minute by minute</span>
        <span className="text-muted">0 – {minutes} min</span>
      </figcaption>
      <div className="flex h-3 overflow-hidden rounded-full bg-surface" role="img" aria-label="Share of good posture per minute">
        {Array.from({ length: minutes }, (_, m) => {
          const p = byMinute.get(m);
          const shade = !p || p.valid < 0.3 ? 'transparent' : `color-mix(in srgb, var(--color-state-good) ${Math.round(p.good * 100)}%, var(--color-state-slouch-soft))`;
          return <span key={m} className="h-full flex-1" style={{ backgroundColor: shade }} />;
        })}
      </div>
    </figure>
  );
}

// ── skeleton snapshot ─────────────────────────────────────────────────────

export function SnapshotCard({
  snap,
  startedAt,
  onOpen,
}: {
  snap: Snapshot;
  startedAt: number;
  onOpen?: () => void;
}): ReactNode {
  const clock = new Date(startedAt + snap.blockStart).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const color = scoreColor(snap.blockScore);
  // Spans, not paragraphs: the card is a button when the replay can open from it.
  const body = (
    <>
      <span className="block text-xs font-semibold text-ink">{clock}</span>
      <Skeleton lm={snap.lm} />
      <span className="block text-[11px] leading-tight text-ink-soft">
        Neck {snap.cva == null ? '–' : `${snap.cva.toFixed(0)}°`}
        <br />
        Lean {snap.trunk === null ? '–' : `${snap.trunk.toFixed(0)}°`}
      </span>
      <span className="block text-sm font-bold" style={{ color }}>
        {snap.blockScore}
      </span>
      <span className="block text-[10px] text-muted">
        {Math.round(snap.blockStart / 60_000)}–{Math.round(snap.blockEnd / 60_000)} min
      </span>
    </>
  );
  const className = 'w-28 shrink-0 snap-start space-y-1 rounded-xl bg-surface p-2 text-center';
  return onOpen ? (
    <button type="button" onClick={onOpen} className={`${className} transition active:scale-[0.98]`}>
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}

const SIDES = {
  left: { ear: LM.LEFT_EAR, shoulder: LM.LEFT_SHOULDER, elbow: LM.LEFT_ELBOW, wrist: LM.LEFT_WRIST, hip: LM.LEFT_HIP, knee: LM.LEFT_KNEE, ankle: LM.LEFT_ANKLE },
  right: { ear: LM.RIGHT_EAR, shoulder: LM.RIGHT_SHOULDER, elbow: LM.RIGHT_ELBOW, wrist: LM.RIGHT_WRIST, hip: LM.RIGHT_HIP, knee: LM.RIGHT_KNEE, ankle: LM.RIGHT_ANKLE },
} as const;

/**
 * The camera-side skeleton, mirrored so the person always faces right, fitted
 * to the card. Landmarks are normalised to the frame, so x is scaled by the
 * portrait 3:4 aspect the camera asks for before anything is measured.
 */
export function Skeleton({ lm }: { lm: Int16Array }): ReactNode {
  const shape = useMemo(() => {
    const points = dequantize(lm);
    const sideKey =
      (points[LM.LEFT_EAR]?.visibility ?? 0) >= (points[LM.RIGHT_EAR]?.visibility ?? 0) ? 'left' : 'right';
    const idx = SIDES[sideKey];
    const aspect = PORTRAIT_IDEAL.width / PORTRAIT_IDEAL.height;
    const get = (i: number): { x: number; y: number; ok: boolean } => {
      const p = points[i];
      return { x: (p?.x ?? 0) * aspect, y: p?.y ?? 0, ok: (p?.visibility ?? 0) > 0.3 };
    };
    const ear = get(idx.ear);
    const nose = get(LM.NOSE);
    const facingLeft = nose.x < ear.x;
    const named = {
      ear,
      shoulder: get(idx.shoulder),
      elbow: get(idx.elbow),
      wrist: get(idx.wrist),
      hip: get(idx.hip),
      knee: get(idx.knee),
      ankle: get(idx.ankle),
    };
    const used = Object.values(named).filter((p) => p.ok);
    if (used.length < 3) return null;
    const xs = used.map((p) => p.x);
    const ys = used.map((p) => p.y);
    const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const span = Math.max(maxX - minX, maxY - minY) || 1;
    const pad = 14;
    const scale = (100 - 2 * pad) / span;
    const map = (p: { x: number; y: number }): { x: number; y: number } => {
      const nx = (p.x - minX) * scale + pad + ((100 - 2 * pad) - (maxX - minX) * scale) / 2;
      return { x: facingLeft ? 100 - nx : nx, y: (p.y - minY) * scale + pad + ((100 - 2 * pad) - (maxY - minY) * scale) / 2 };
    };
    const bones: Array<[keyof typeof named, keyof typeof named]> = [
      ['ear', 'shoulder'],
      ['shoulder', 'hip'],
      ['hip', 'knee'],
      ['knee', 'ankle'],
      ['shoulder', 'elbow'],
      ['elbow', 'wrist'],
    ];
    const lines = bones
      .filter(([a, b]) => named[a].ok && named[b].ok)
      .map(([a, b]) => [map(named[a]), map(named[b])] as const);
    const head = ear.ok ? map(ear) : null;
    const torso = named.shoulder.ok && named.hip.ok
      ? Math.hypot(named.shoulder.x - named.hip.x, named.shoulder.y - named.hip.y) * scale
      : null;
    const headR = torso === null ? 7 : Math.min(16, Math.max(6, torso * HEAD_PER_TORSO));
    return { lines, head, headR };
  }, [lm]);

  if (!shape) return <div className="aspect-square w-full rounded-lg bg-card" />;
  return (
    <svg viewBox="0 0 100 100" className="aspect-square w-full rounded-lg bg-card" role="img" aria-label="Skeleton at this point">
      <g stroke="var(--color-ink)" strokeWidth={4} strokeLinecap="round" fill="none">
        {shape.lines.map(([a, b], i) => (
          <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
        ))}
        {shape.head ? <circle cx={shape.head.x} cy={shape.head.y} r={shape.headR} /> : null}
      </g>
    </svg>
  );
}

